import { useState } from 'react'
import { ShieldAlert } from 'lucide-react'

import { Input, Select } from '@/components/ui/input'
import { cn } from '@/lib/cn'

/**
 * Retention applied to newly uploaded objects, sent as X-HCP-RETENTION.
 *
 * Values use HCP's offset form, `A+<n><unit>`, where the unit is one of
 * y (years), M (months), d (days), h (hours) or m (minutes). HCP resolves the
 * offset itself, so no clock skew between this app and the platform can shift
 * the expiry.
 *
 * Grouped rather than flat because the list is long enough that scanning it
 * matters, and because the jump from days to years is a meaningful step up in
 * consequence.
 */
export const RETENTION_GROUPS: { label: string | null; options: { value: string; label: string }[] }[] =
  [
    { label: null, options: [{ value: '', label: 'No retention' }] },
    {
      label: 'Hours',
      options: [
        { value: 'A+1h', label: '1 hour' },
        { value: 'A+3h', label: '3 hours' },
        { value: 'A+6h', label: '6 hours' },
        { value: 'A+12h', label: '12 hours' },
      ],
    },
    {
      label: 'Days',
      options: [
        { value: 'A+1d', label: '1 day' },
        { value: 'A+5d', label: '5 days' },
        { value: 'A+7d', label: '7 days' },
        { value: 'A+10d', label: '10 days' },
        { value: 'A+14d', label: '14 days' },
        { value: 'A+30d', label: '30 days' },
      ],
    },
    {
      label: 'Months',
      options: [
        { value: 'A+3M', label: '3 months' },
        { value: 'A+6M', label: '6 months' },
      ],
    },
    {
      label: 'Years',
      options: [
        { value: 'A+1y', label: '1 year' },
        { value: 'A+2y', label: '2 years' },
        { value: 'A+5y', label: '5 years' },
        { value: 'A+10y', label: '10 years' },
      ],
    },
    {
      label: 'Other',
      options: [
        { value: '-1', label: 'Indefinite — never deletable' },
        { value: 'custom', label: 'Custom…' },
      ],
    },
  ]

const ALL_PRESETS = RETENTION_GROUPS.flatMap((group) => group.options)

/** Human-readable summary of the selected value, for the warning line. */
function describe(value: string): string {
  if (value === '-1') return 'never be deletable'
  const preset = ALL_PRESETS.find((option) => option.value === value)
  return preset ? `be undeletable for ${preset.label}` : 'be undeletable until the retention expires'
}

export function RetentionPicker({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const isPreset = ALL_PRESETS.some((p) => p.value === value && p.value !== 'custom')
  const [custom, setCustom] = useState(!isPreset && value !== '')

  const active = value !== ''

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor="retention"
          className={cn(
            'flex items-center gap-1.5 text-sm',
            active ? 'font-medium text-warning' : 'text-muted-foreground',
          )}
        >
          {active && <ShieldAlert className="h-4 w-4" />}
          Retention
        </label>

        <Select
          id="retention"
          disabled={disabled}
          className="h-8 w-auto min-w-44 text-sm"
          value={custom ? 'custom' : value}
          onChange={(e) => {
            if (e.target.value === 'custom') {
              setCustom(true)
              onChange('')
            } else {
              setCustom(false)
              onChange(e.target.value)
            }
          }}
        >
          {RETENTION_GROUPS.map((group, index) =>
            group.label === null ? (
              group.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            ) : (
              <optgroup key={group.label ?? index} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ),
          )}
        </Select>

        {custom && (
          <Input
            className="h-8 w-52 font-mono text-xs"
            placeholder="A+45d, 1893456000, C+Class"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Custom retention value"
          />
        )}
      </div>

      {custom && (
        <p className="text-xs text-muted-foreground">
          Accepts an offset (<code className="font-mono">A+45d</code>, units{' '}
          <code className="font-mono">y M d h m</code>), Unix epoch seconds, or a retention
          class (<code className="font-mono">C+Name</code>).
        </p>
      )}

      {active && (
        <p className="text-xs text-warning">
          Uploaded files will {describe(value)}.
        </p>
      )}
    </div>
  )
}
